import type { CategoryId, CategoryPlaybook } from "../types";
import { THREE_PL_PLAYBOOK } from "./threePL";
import { SAME_DAY_COURIER_PLAYBOOK } from "./sameDayCourier";

/**
 * Category playbook registry.
 *
 * Load order for the pack generator:
 *   1. Exact match playbook (known: true) — full scope/matrix/SLA/pricing seeds.
 *   2. "other" — universal skeleton with TBD banner (known: false).
 *
 * The generator treats category as data: it never hardcodes nouns from a
 * specific category into prompts or section templates beyond what the
 * selected playbook provides.
 */

const REGISTRY: Partial<Record<CategoryId, CategoryPlaybook>> = {
  "3pl_contract_logistics": THREE_PL_PLAYBOOK,
  "same_day_courier": SAME_DAY_COURIER_PLAYBOOK,
};

/**
 * Domain classification for the strict vocabulary + section guardrails.
 *
 * - "supply_chain" — physical logistics/3PL categories (known playbooks and
 *   any custom category whose name/brief signals freight, warehouse, courier,
 *   distribution, fulfilment or similar). Sections 4/9/10 keep the transport
 *   and supply-chain wording; OTIF, inventory-accuracy, hazmat etc. are valid.
 * - "services" — indirect / services / digital categories (Digital Marketing,
 *   IT Managed Services, Professional Services, consulting, facilities, …).
 *   All logistics/3PL terminology is purged and replaced with universal
 *   service KPIs (deliverable timeliness, SLA compliance, brand safety,
 *   campaign ROI/ROAS, data privacy compliance) and IP / ad-fraud / SOC 2
 *   security & ESG emphasis.
 */
export type CategoryDomain = "supply_chain" | "services";

const SUPPLY_CHAIN_SIGNALS =
  /\b(?:logistics|3pl\b|third[- ]party\s+logistics|warehouse|warehousing|freight|transport|distribution|fulfil?ment|courier|same[- ]day|parcel|pallets?|cold[- ]chain|supply[- ]chain|forwarding|shipping|despatch|inventory|packaging|drivers?|fleet|carrier)\b/i;
const SERVICES_SIGNALS =
  /\b(?:market(?:ing)?|advertis|media|digital|brand|creative|design|content|it\b|technology|software|saas|cloud\b|cyber|data\b|professional|consulting|legal\b|accounting|finance\b|insurance|hr\b|recruit|payroll|training|learning|facilities|cleaning|catering|security\s+services|customer\s+service|call\s+centre|support\s+services|events?|research|analytics|pr\b|public\s+relations|campaign|seo|social\s+media|app\b|development|managed\s+services)\b/i;

// Services sub-domain routing used by the fallback matrix logic. IT,
// Marketing/Media and Professional Services get distinct skeletons while
// every other services category shares the generic universal set. This is
// what keeps category-specific requirements (brand-safety / ad-fraud, IT
// availability / resilience, professional confidentiality) isolated: each
// row set exists in exactly one skeleton.
export type ServicesSubdomain = "it" | "marketing" | "professional" | "generic";

const IT_SUBDOMAIN_SIGNALS =
  /\b(?:it\b|managed\s+services|it\s+support|service\s*desk|help\s*desk|infrastructure|network|cloud\b|saas\b|software|application\s*development|app\s*development|devops|cyber|security\s*operations|data\s*center|ict\b|systems\s*integration|technology)\b/i;
const MARKETING_SUBDOMAIN_SIGNALS =
  /\b(?:market(?:ing)?|media\b|advertis|ad[\s-]?ops|programmatic|seo\b|\bsem\b|\bppc\b|social\s*media|content\b|creative|brand(?:ing)?|\bpr\b|public\s*relations|campaign|performance\s*marketing|growth)\b/i;
const PROFESSIONAL_SUBDOMAIN_SIGNALS =
  /\b(?:consult(?:ing|ancy)?|legal\b|law\b|attorney|solicitor|account(?:ing|ancy)|audit\b|tax\b|hr\b|human\s*resources|recruit|talent\b|payroll|training\b|advisory|executive\s*search)\b/i;

const SUPPLY_CHAIN_CATEGORIES = new Set<CategoryId>([
  "3pl_contract_logistics",
  "same_day_courier",
  "warehousing",
  "freight_transport",
  "tms_visibility",
  "wms_inventory",
  "mro_indirect",
  "packaging",
  "contract_manufacturing",
]);

/**
 * Decide whether a category (and its custom name + typed brief) is a
 * services / indirect category (strict logistics-vocabulary purge applies)
 * or a supply-chain category. Explicitly-named logistics categories are
 * always supply_chain; custom "Other" categories default to services unless
 * their name or brief signals freight, warehouse, courier, trucks, etc.
 */
export function categoryDomain(
  category: CategoryId,
  customCategoryName = "",
  brief = "",
): CategoryDomain {
  if (SUPPLY_CHAIN_CATEGORIES.has(category)) return "supply_chain";
  const signalSrc = `${customCategoryName} ${brief}`.trim();
  if (!signalSrc) return "services";
  const scHit = SUPPLY_CHAIN_SIGNALS.test(signalSrc);
  const svHit = SERVICES_SIGNALS.test(signalSrc);
  if (scHit && !svHit) return "supply_chain";
  return "services";
}

/**
 * Sub-domain routing for services categories. Checked in priority order so a
 * name like "IT managed services" lands in the IT skeleton and "digital
 * marketing" in the marketing one. Used only to pick the fallback skeleton;
 * the coarse services / supply-chain split above still drives the purge.
 */
export function servicesSubdomain(
  category: CategoryId,
  customCategoryName = "",
  brief = "",
): ServicesSubdomain {
  const src = `${customCategoryName} ${brief}`.trim();
  if (!src) return "generic";
  if (IT_SUBDOMAIN_SIGNALS.test(src)) return "it";
  if (MARKETING_SUBDOMAIN_SIGNALS.test(src)) return "marketing";
  if (PROFESSIONAL_SUBDOMAIN_SIGNALS.test(src)) return "professional";
  return "generic";
}

/**
 * Universal skeleton for categories without a specialist playbook yet.
 * Scope seeds are deliberately generic; the user must review the matrix
 * carefully (an unknown-category banner is shown on the pack canvas).
 */
const UNIVERSAL_SKELETON_FACTORY = (id: CategoryId, label: string): CategoryPlaybook => ({
  id: id === "other" ? ("other" as CategoryId) : id,
  label,
  description:
    "No specialist playbook is available for this category yet. The pack below is generated from the universal supply-chain skeleton and the answers you gave. Review the compliance matrix carefully before issuing.",
  scope_seeds: {
    in_scope: [
      "Supplier services covered by this RFP as defined in Section 2 (Scope)",
      "Transition / implementation activities to go live by the stated date",
      "Ongoing service delivery and reporting over the stated contract term",
    ],
    out_of_scope: [
      "Items explicitly excluded in Section 2",
      "Buyer systems replacement unless stated",
    ],
  },
  scope_tags: ["all"],
  matrix: [
    { id: "REQ-OPS-001", category: "Operations", requirement: "Clear service description for the category in scope", mandatory: true, evidence: "Service description", maps_to: "technical_fit", scope_tags: ["all"] },
    { id: "REQ-OPS-002", category: "Operations", requirement: "Measurable service level for the primary outcome", mandatory: true, evidence: "KPI definition", maps_to: "technical_fit", scope_tags: ["all"] },
    { id: "REQ-OPS-003", category: "Operations", requirement: "Reporting of the agreed KPI set at stated cadence", mandatory: true, evidence: "Report template", maps_to: "capability", scope_tags: ["all"] },
    { id: "REQ-IT-020", category: "IT / Systems", requirement: "Data access and integration approach for the buyer's systems", mandatory: true, evidence: "Interface plan", maps_to: "technical_fit", scope_tags: ["all"] },
    { id: "REQ-SLA-010", category: "Service Level", requirement: "Service review cadence agreed with the buyer", mandatory: true, evidence: "Review agenda", maps_to: "capability", scope_tags: ["all"] },
    { id: "REQ-COM-050", category: "Commercial", requirement: "Complete commercial proposal on the supplied pricing template", mandatory: true, evidence: "Completed template", maps_to: "commercial", scope_tags: ["all"] },
    { id: "REQ-RSK-050", category: "Risk / Compliance", requirement: "Insurance minimums stated and evidenced", mandatory: true, evidence: "Insurance certificate", maps_to: "risk", scope_tags: ["all"] },
    { id: "REQ-RSK-051", category: "Risk / Compliance", requirement: "Business continuity and exit / transition arrangements", mandatory: true, evidence: "BCP summary + transition plan", maps_to: "risk", scope_tags: ["all"] },
  ],
  pricing_template: {
    sheet: "Commercial Proposal",
    sections: [
      { name: "Service pricing", rows: ["Base fee or unit rates for each stated service", "Volume-based bands if applicable"] },
      { name: "Implementation", rows: ["One-off implementation / setup charges"] },
      { name: "Accessorials", rows: ["Published accessorial and change-charge menu"] },
    ],
  },
  sla_catalogue: [
    { metric: "Primary outcome service level", target: "[TBD] — agree with playbook creator", evidence: "[TBD]", mandatory: true },
    { metric: "Quality / defect measure", target: "[TBD]", evidence: "[TBD]", mandatory: false },
  ],
  rubric_anchors: [],
  legal_flags_hints: [],
  insurance_minimums: [],
});

// Fallback anchors for unknown categories (cannot reference literal above)
const FALLBACK_ANCHORS = [
  { dimension: "technical_fit", score: 5, anchor: "Full compliance with mandatory rows and evidence" },
  { dimension: "technical_fit", score: 3, anchor: "Meets mandatory rows; minor clarification needed" },
  { dimension: "technical_fit", score: 1, anchor: "Fails a mandatory requirement or evidence missing" },
  { dimension: "approach", score: 5, anchor: "Clear plan that meets the stated problem" },
  { dimension: "approach", score: 3, anchor: "Sound plan; light on risks or detail" },
  { dimension: "approach", score: 1, anchor: "Does not demonstrate understanding of the problem" },
  { dimension: "commercial", score: 5, anchor: "Complete, transparent commercial proposal" },
  { dimension: "commercial", score: 3, anchor: "Complete but with conditions or gaps" },
  { dimension: "commercial", score: 1, anchor: "Incomplete or off-template pricing" },
  { dimension: "capability", score: 5, anchor: "Good references and capacity confirmed" },
  { dimension: "capability", score: 3, anchor: "Relevant experience; mixed references" },
  { dimension: "capability", score: 1, anchor: "No relevant references" },
  { dimension: "risk", score: 5, anchor: "Evidence provided; low residual risk" },
  { dimension: "risk", score: 3, anchor: "Mostly evidenced; manageable open items" },
  { dimension: "risk", score: 1, anchor: "Missing insurance/BCP/exit evidence" },
];

const CATEGORY_LABELS: Record<CategoryId, string> = {
  "3pl_contract_logistics": "3PL / contract logistics",
  same_day_courier: "Same-day courier / radiopharmaceutical logistics",
  warehousing: "Warehousing / fulfilment",
  freight_transport: "Freight / transportation",
  tms_visibility: "TMS / visibility platform",
  wms_inventory: "WMS / inventory system",
  mro_indirect: "MRO / indirect materials",
  packaging: "Packaging",
  contract_manufacturing: "Contract manufacturing / co-packer",
  other: "Other / custom category",
};

/**
 * Universal services / indirect skeleton for non-supply-chain categories
 * whose sub-domain is neither IT, Marketing/Media nor Professional Services.
 * Matrix and SLA seeds use universal service/domain KPIs — deliverable
 * timeliness, SLA compliance and data privacy compliance — never
 * supply-chain vocabulary and never sub-domain-specific rows. Brand-safety,
 * ad-fraud, IT-resilience and professional-conduct requirements are owned by
 * their own sub-domain factories below, not this set.
 */
const SERVICES_GENERIC_SKELETON_FACTORY = (id: CategoryId, label: string): CategoryPlaybook => ({
  id,
  label,
  description:
    "A universal services skeleton is used for this indirect/services category. The pack is generated from the service-level KPI set and the answers you gave. Review the compliance matrix carefully before issuing.",
  scope_seeds: {
    in_scope: [
      "Services covered by this RFP as defined in Section 2 (Scope)",
      "Transition / on-boarding activities to go live by the stated date",
      "Ongoing service delivery and reporting over the stated term",
    ],
    out_of_scope: [
      "Items explicitly excluded in Section 2",
      "Buyer systems replacement unless stated",
    ],
  },
  scope_tags: ["all"],
  matrix: [
    { id: "REQ-SVC-001", category: "Operations", requirement: "Clear service description for the category in scope", mandatory: true, evidence: "Service description", maps_to: "technical_fit", scope_tags: ["all"] },
    { id: "REQ-SVC-002", category: "Service Level", requirement: "Measurable service level for the primary deliverable, e.g. deliverable timeliness and SLA compliance", mandatory: true, evidence: "KPI definition", maps_to: "technical_fit", scope_tags: ["all"] },
    { id: "REQ-SVC-003", category: "Service Level", requirement: "Reporting of the agreed KPI set at stated cadence", mandatory: true, evidence: "Report template", maps_to: "capability", scope_tags: ["all"] },
    { id: "REQ-SVC-004", category: "Service Level", requirement: "Service review cadence agreed with the buyer", mandatory: true, evidence: "Review agenda", maps_to: "capability", scope_tags: ["all"] },
    { id: "REQ-SVC-005", category: "Operations", requirement: "Quality / rework / defect measure for deliverables", mandatory: false, evidence: "QA procedure", maps_to: "technical_fit", scope_tags: ["all"] },
    { id: "REQ-DATA-010", category: "Data / Privacy", requirement: "Data privacy compliance for any personal data in scope (GDPR / CCPA where applicable)", mandatory: true, evidence: "DPA + privacy notice", maps_to: "risk", scope_tags: ["all"] },
    { id: "REQ-IP-020", category: "Legal / IP", requirement: "Clear ownership of intellectual property in deliverables and any buyer-supplied materials", mandatory: true, evidence: "IP assignment clause", maps_to: "risk", scope_tags: ["all"] },
    { id: "REQ-IT-050", category: "IT / Cyber", requirement: "Cybersecurity posture and data-access controls for any buyer systems or data accessed (SOC 2 / ISO 27001 or equivalent)", mandatory: true, evidence: "Security attestation", maps_to: "risk", scope_tags: ["all"] },
    { id: "REQ-COM-060", category: "Commercial", requirement: "Complete commercial proposal on the supplied pricing template", mandatory: true, evidence: "Completed template", maps_to: "commercial", scope_tags: ["all"] },
    { id: "REQ-RSK-070", category: "Risk / Compliance", requirement: "Insurance minimums stated and evidenced", mandatory: true, evidence: "Insurance certificate", maps_to: "risk", scope_tags: ["all"] },
    { id: "REQ-RSK-071", category: "Risk / Compliance", requirement: "Business continuity and exit / transition arrangements", mandatory: true, evidence: "BCP summary + transition plan", maps_to: "risk", scope_tags: ["all"] },
  ],
  pricing_template: {
    sheet: "Service Proposal",
    sections: [
      { name: "Service pricing", rows: ["Base fee or unit rates for each stated deliverable", "Volume / engagement-based bands if applicable"] },
      { name: "Implementation", rows: ["One-off on-boarding / setup charges"] },
      { name: "Accessorials", rows: ["Published out-of-scope and change-charge menu"] },
    ],
  },
  sla_catalogue: [
    { metric: "Deliverable timeliness", target: "[to be confirmed by the supplier]", evidence: "Delivery log", mandatory: true },
    { metric: "SLA compliance", target: "[to be confirmed by the supplier]", evidence: "SLA report", mandatory: true },
    { metric: "Quality / defect measure", target: "[to be confirmed]", evidence: "[to be confirmed]", mandatory: false },
    { metric: "Reporting cadence", target: "as agreed with the buyer", evidence: "Report template", mandatory: false },
  ],
  rubric_anchors: [],
  legal_flags_hints: [
    "PII / GDPR·CCPA — confirm whether personal data is in scope and the processing register before issue",
    "IP ownership — deliverables and buyer-supplied materials",
    "Security — SOC 2 / ISO 27001 (or equivalent) where systems or data are accessed",
  ],
  insurance_minimums: [
    "Professional indemnity: cover appropriate to the scope of services, evidenced by certificate at proposal",
    "Cyber / data breach cover where personal data is processed, evidenced by certificate at proposal",
  ],
});

/**
 * Marketing / Media skeleton. The only place REQ-BRAND-030 (brand safety) and
 * REQ-ADFRAUD-040 (ad fraud / invalid traffic) exist — deliberately absent
 * from the IT, Professional and generic services skeletons so marketing
 * clauses never leak into an IT, software, consulting or legal pack.
 */
const MARKETING_SKELETON_FACTORY = (id: CategoryId, label: string): CategoryPlaybook => {
  const base = SERVICES_GENERIC_SKELETON_FACTORY(id, label);
  return {
    ...base,
    description:
      "A digital marketing / media skeleton is used for this category, adding brand-safety and invalid-traffic controls to the universal service-level KPI set. Review the compliance matrix carefully before issuing.",
    matrix: [
      ...base.matrix,
      { id: "REQ-BRAND-030", category: "Brand Safety", requirement: "Brand-safety and content-governance controls for externally published deliverables", mandatory: false, evidence: "Brand safety policy", maps_to: "risk", scope_tags: ["all"] },
      { id: "REQ-ADFRAUD-040", category: "Media / Ad Ops", requirement: "Invalid-traffic / ad-fraud controls where paid media is in scope", mandatory: false, evidence: "IVT verification report", maps_to: "risk", scope_tags: ["all"] },
    ],
    sla_catalogue: [
      ...base.sla_catalogue,
      { metric: "Campaign reporting & delivery", target: "delivered at the agreed cadence with media-quality breakdown", evidence: "Campaign report", mandatory: false },
      { metric: "Invalid-traffic / IVT discrepancy", target: "within buyer-approved thresholds [supplier to confirm]", evidence: "IVT verification report", mandatory: false },
    ],
    legal_flags_hints: [
      "PII / GDPR·CCPA — confirm whether personal data is in scope and the processing register before issue",
      "IP ownership — deliverables and buyer-supplied materials",
      "Ad fraud & brand safety — applicable for paid media / content engagements",
      "Security — SOC 2 / ISO 27001 (or equivalent) where systems or data are accessed",
    ],
  };
};

/**
 * IT Managed Services / Software / Infrastructure skeleton. Adds availability
 * (uptime), incident response (P1 / P2), backup & DR (RPO / RTO), monitoring
 * and MFA access-control rows to the universal services set. Used whenever
 * the custom category or brief signals IT / managed services / cloud /
 * software / cyber. No brand-safety or ad-fraud rows.
 */
const IT_SKELETON_FACTORY = (id: CategoryId, label: string): CategoryPlaybook => {
  const base = SERVICES_GENERIC_SKELETON_FACTORY(id, label);
  return {
    ...base,
    description:
      "An IT managed services skeleton is used for this category, adding availability, incident-response, resilience and access-control KPIs to the universal service-level set. Review the compliance matrix carefully before issuing.",
    matrix: [
      ...base.matrix,
      { id: "REQ-IT-051", category: "IT / Availability", requirement: "Service availability target for the production service agreed with the buyer (e.g. ≥ 99.9% uptime per month)", mandatory: true, evidence: "Availability SLA", maps_to: "technical_fit", scope_tags: ["all"] },
      { id: "REQ-IT-052", category: "IT / Availability", requirement: "Incident response committed — P1 response within stated minutes, P2 within agreed window, with post-incident review", mandatory: true, evidence: "Incident SLA + PIR template", maps_to: "technical_fit", scope_tags: ["all"] },
      { id: "REQ-IT-053", category: "IT / Resilience", requirement: "Backup and disaster-recovery commitments including RPO / RTO for the data in scope, with monitoring and periodic DR test evidence", mandatory: true, evidence: "BCP / DR plan + test log", maps_to: "risk", scope_tags: ["all"] },
      { id: "REQ-IT-054", category: "IT / Access", requirement: "Access controls — least-privilege, multi-factor authentication (MFA) and change management for any buyer systems accessed", mandatory: true, evidence: "Access policy + MFA evidence", maps_to: "risk", scope_tags: ["all"] },
    ],
    sla_catalogue: [
      { metric: "Service availability (uptime)", target: "≥ 99.9% per month [supplier to confirm target with evidence]", evidence: "Uptime report", mandatory: true },
      { metric: "Incident response — P1", target: "respond within 15 minutes; resolve within 4 hours [supplier to confirm]", evidence: "Incident log", mandatory: true },
      { metric: "Incident response — P2", target: "respond within 30 minutes; resolve within 24 hours [supplier to confirm]", evidence: "Incident log", mandatory: false },
      { metric: "Backup & recovery (RPO / RTO)", target: "RPO ≤ 4 hours; RTO ≤ 12 hours for data in scope [supplier to confirm]", evidence: "DR plan + test log", mandatory: true },
      { metric: "Monitoring & alerting", target: "24×7 monitoring of the components in scope", evidence: "Monitoring scope", mandatory: false },
      { metric: "Reporting cadence", target: "as agreed with the buyer", evidence: "Report template", mandatory: false },
    ],
    legal_flags_hints: [
      "PII / GDPR·CCPA — confirm whether personal data is in scope and the processing register before issue",
      "IP ownership — deliverables and buyer-supplied materials",
      "Security — SOC 2 Type II / ISO 27001 (or equivalent) attestation and MFA for access",
      "Data residency & recovery — confirm data residency and RPO / RTO commitments",
    ],
  };
};

/**
 * Professional Services skeleton (consulting / legal / accounting / HR …).
 * Keeps the universal SLA / deliverables emphasis and adds confidentiality,
 * conflicts-of-interest and named-team rows. No brand-safety or ad-fraud rows.
 */
const PROFESSIONAL_SKELETON_FACTORY = (id: CategoryId, label: string): CategoryPlaybook => {
  const base = SERVICES_GENERIC_SKELETON_FACTORY(id, label);
  return {
    ...base,
    description:
      "A professional services skeleton is used for this category, adding confidentiality, conflicts and named-team requirements to the universal SLA / deliverables KPI set. Review the compliance matrix carefully before issuing.",
    matrix: [
      ...base.matrix,
      { id: "REQ-PRO-010", category: "Professional", requirement: "Confidentiality of buyer information, conflicts-of-interest disclosure and professional standards agreed", mandatory: true, evidence: "Confidentiality terms + conflicts register", maps_to: "risk", scope_tags: ["all"] },
      { id: "REQ-PRO-020", category: "Professional", requirement: "Named delivery team with key-person commitment and continuity plan", mandatory: false, evidence: "Team roster + CVs", maps_to: "capability", scope_tags: ["all"] },
    ],
    legal_flags_hints: [
      "PII / GDPR·CCPA — confirm whether personal data is in scope and the processing register before issue",
      "IP ownership — deliverables and buyer-supplied materials",
      "Confidentiality & conflicts — professional standards, independence and conflicts-of-interest register",
      "Security — SOC 2 / ISO 27001 (or equivalent) where systems or data are accessed",
    ],
  };
};

function createUnknownPlaybook(id: CategoryId, customCategoryName = "", brief = ""): CategoryPlaybook {
  const entry = REGISTRY[id];
  if (entry) return entry;
  const domain = categoryDomain(id, customCategoryName);
  const label = domain === "services"
    ? customCategoryName || "Services / indirect"
    : customCategoryName || (CATEGORY_LABELS[id] ?? "Other / custom category");
  if (domain === "services") {
    const sub = servicesSubdomain(id, customCategoryName, brief);
    if (sub === "it") return IT_SKELETON_FACTORY(id, label);
    if (sub === "marketing") return MARKETING_SKELETON_FACTORY(id, label);
    if (sub === "professional") return PROFESSIONAL_SKELETON_FACTORY(id, label);
    return SERVICES_GENERIC_SKELETON_FACTORY(id, label);
  }
  const skeleton = UNIVERSAL_SKELETON_FACTORY(id, label);
  return {
    ...skeleton,
    rubric_anchors: [...FALLBACK_ANCHORS],
    legal_flags_hints: [
      "Insurance minimums — confirm with legal before issuing",
      "GDPR / data processing — confirm whether personal data is in scope",
      "Security requirements — SOC2 or equivalent if systems are involved",
    ],
    insurance_minimums: [
      "Professional / general liability: cover appropriate to the scope of operations, evidenced by certificate at proposal",
      "Employer's liability: cover appropriate to the scope of operations, evidenced by certificate at proposal",
    ],
    scope_tags: ["all"],
  };
}

export function getPlaybook(id: CategoryId, customCategoryName = "", brief = ""): CategoryPlaybook {
  return createUnknownPlaybook(id, customCategoryName, brief);
}

export function isKnownCategory(id: CategoryId): boolean {
  return id in REGISTRY && REGISTRY[id]?.known === true;
}

export function categoryLabel(id: CategoryId, customCategoryName = ""): string {
  if (customCategoryName && id === "other") return customCategoryName;
  if (customCategoryName && !(id in REGISTRY)) return customCategoryName;
  return CATEGORY_LABELS[id] ?? "Other / custom category";
}